import React from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { ThemeProvider } from "@/hooks/use-theme";
import { HelmetProvider } from "react-helmet-async";
import { lazy, Suspense, useEffect } from "react";
import { ErrorBoundary } from "@/components/ErrorBoundary";

import Index from "./pages/Index";
import NotFound from "./pages/NotFound";
import { SiteChatbot } from "./components/SiteChatbot";

const queryClient = new QueryClient();

const PageLoader = () => (
  <div className="min-h-screen bg-background flex items-center justify-center">
    <div className="animate-pulse text-muted-foreground">Loading...</div>
  </div>
);

// Wrap lazy imports with error handling to catch failed chunk loads
const safeLazy = (importFn: () => Promise<{ default: React.ComponentType<object> }>) => {
  return lazy(() => 
    importFn().catch((err) => {
      console.error("Failed to load page component:", err);
      return { 
        default: () => (
          <div className="min-h-screen bg-background flex items-center justify-center">
            <div className="text-center">
              <p className="text-destructive mb-4">Failed to load page.</p>
              <button 
                onClick={() => window.location.reload()} 
                className="text-primary underline"
              >
                Click to refresh
              </button>
            </div>
          </div>
        )
      };
    })
  );
};

// Lazy load hub pages with error handling
const SalaryHub = safeLazy(() => import("./pages/SalaryHub"));
const InterviewPrepHubPage = safeLazy(() => import("./pages/InterviewPrepHubPage"));
const HiringPlaybooksHub = safeLazy(() => import("./pages/HiringPlaybooksHub"));
const ToolsHub = safeLazy(() => import("./pages/ToolsHub"));
const HiringTrends = safeLazy(() => import("./pages/HiringTrends"));
const AboutPage = safeLazy(() => import("./pages/AboutPage"));
const ContactPage = safeLazy(() => import("./pages/ContactPage"));
const PrivacyPage = safeLazy(() => import("./pages/PrivacyPage"));
const SecurityPage = safeLazy(() => import("./pages/SecurityPage"));
const MethodologyPage = safeLazy(() => import("./pages/MethodologyPage"));
const PodcastPage = safeLazy(() => import("./pages/PodcastPage"));
const CVReviewerPage = safeLazy(() => import("./pages/CVReviewerPage"));
const NewslettersPage = safeLazy(() => import("./pages/NewslettersPage"));
const PythonNewsletterPage = safeLazy(() => import("./pages/PythonNewsletterPage"));
const PythonHiringNewsletterPage = safeLazy(() => import("./pages/PythonHiringNewsletterPage"));
const PythonStackGuidesPage = safeLazy(() => import("./pages/PythonStackGuidesPage"));
const EditorialPolicyPage = safeLazy(() => import("./pages/EditorialPolicyPage"));
const TermsPage = safeLazy(() => import("./pages/TermsPage"));
const LinkedInAnalyzerPage = safeLazy(() => import("./pages/LinkedInAnalyzerPage"));

// Hash redirect component for SEO
const HashRedirect = () => {
  const location = useLocation();
  
  useEffect(() => {
    const hashRedirects: Record<string, string> = {
      '#salary-guide': '/salary/',
      '#interview-prep': '/interview-prep/',
      '#hiring-playbooks': '/hiring-playbooks/',
      '#cv-analyzer': '/cv-reviewer/',
      '#cv-analyser': '/cv-reviewer/',
      '#market-signals': '/hiring-trends/',
      '#podcast': '/podcast/',
      '#about': '/about/',
    };
    
    if (location.hash && hashRedirects[location.hash]) {
      window.location.replace(hashRedirects[location.hash]);
    }
  }, [location]);
  
  return null;
};

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <ThemeProvider defaultTheme="dark" storageKey="snake-signals-theme">
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <HashRedirect />
            <ErrorBoundary>
            <Suspense fallback={<PageLoader />}>
              <Routes>
                <Route path="/" element={<Index />} />
                {/* Theme preview routes */}
                <Route path="/__theme/light" element={<Index />} />
                <Route path="/__theme/dark" element={<Index />} />
                {/* Main hub pages */}
                <Route path="/salary" element={<Navigate to="/salary/" replace />} />
                <Route path="/salary/" element={<SalaryHub />} />
                <Route path="/salary/*" element={<SalaryHub />} />
                <Route path="/salary-guide" element={<Navigate to="/salary/" replace />} />
                <Route path="/interview-prep" element={<Navigate to="/interview-prep/" replace />} />
                <Route path="/interview-prep/" element={<InterviewPrepHubPage />} />
                <Route path="/interview-prep/*" element={<InterviewPrepHubPage />} />
                <Route path="/hiring-playbooks" element={<Navigate to="/hiring-playbooks/" replace />} />
                <Route path="/hiring-playbooks/" element={<HiringPlaybooksHub />} />
                <Route path="/hiring-playbooks/*" element={<HiringPlaybooksHub />} />
                <Route path="/tools" element={<Navigate to="/tools/" replace />} />
                <Route path="/tools/" element={<ToolsHub />} />
                <Route path="/tools/*" element={<ToolsHub />} />
                <Route path="/hiring-trends" element={<Navigate to="/hiring-trends/" replace />} />
                <Route path="/hiring-trends/" element={<HiringTrends />} />
                <Route path="/hiring-trends/*" element={<HiringTrends />} />
                {/* SEO-focused pages */}
                <Route path="/python-newsletter" element={<Navigate to="/python-newsletter/" replace />} />
                <Route path="/python-newsletter/" element={<PythonNewsletterPage />} />
                <Route path="/python-newsletter/*" element={<PythonNewsletterPage />} />
                <Route path="/python-hiring-newsletter" element={<Navigate to="/python-hiring-newsletter/" replace />} />
                <Route path="/python-hiring-newsletter/" element={<PythonHiringNewsletterPage />} />
                <Route path="/python-hiring-newsletter/*" element={<PythonHiringNewsletterPage />} />
                <Route path="/python-stack-guides" element={<Navigate to="/python-stack-guides/" replace />} />
                <Route path="/python-stack-guides/" element={<PythonStackGuidesPage />} />
                <Route path="/python-stack-guides/*" element={<PythonStackGuidesPage />} />
                {/* Archive alias */}
                <Route path="/archive" element={<Navigate to="/newsletters/" replace />} />
                {/* Trust & company pages */}
                <Route path="/about" element={<Navigate to="/about/" replace />} />
                <Route path="/about/" element={<AboutPage />} />
                <Route path="/about/*" element={<AboutPage />} />
                <Route path="/contact" element={<Navigate to="/contact/" replace />} />
                <Route path="/contact/" element={<ContactPage />} />
                <Route path="/contact/*" element={<ContactPage />} />
                <Route path="/privacy" element={<Navigate to="/privacy/" replace />} />
                <Route path="/privacy/" element={<PrivacyPage />} />
                <Route path="/privacy/*" element={<PrivacyPage />} />
                <Route path="/security" element={<Navigate to="/security/" replace />} />
                <Route path="/security/" element={<SecurityPage />} />
                <Route path="/security/*" element={<SecurityPage />} />
                <Route path="/methodology" element={<Navigate to="/methodology/" replace />} />
                <Route path="/methodology/" element={<MethodologyPage />} />
                <Route path="/methodology/*" element={<MethodologyPage />} />
                <Route path="/editorial-policy" element={<Navigate to="/editorial-policy/" replace />} />
                <Route path="/editorial-policy/" element={<EditorialPolicyPage />} />
                <Route path="/editorial-policy/*" element={<EditorialPolicyPage />} />
                <Route path="/terms" element={<Navigate to="/terms/" replace />} />
                <Route path="/terms/" element={<TermsPage />} />
                <Route path="/terms/*" element={<TermsPage />} />
                <Route path="/how-salary-estimates-work" element={<Navigate to="/methodology/" replace />} />
                <Route path="/sources" element={<Navigate to="/methodology/" replace />} />
                <Route path="/podcast" element={<Navigate to="/podcast/" replace />} />
                <Route path="/podcast/" element={<PodcastPage />} />
                <Route path="/podcast/*" element={<PodcastPage />} />
                <Route path="/newsletters" element={<Navigate to="/newsletters/" replace />} />
                <Route path="/newsletters/" element={<NewslettersPage />} />
                <Route path="/newsletters/*" element={<NewslettersPage />} />
                <Route path="/cv-reviewer" element={<Navigate to="/cv-reviewer/" replace />} />
                <Route path="/cv-reviewer/" element={<CVReviewerPage />} />
                <Route path="/cv-reviewer/*" element={<CVReviewerPage />} />
                <Route path="/cv-analyzer" element={<Navigate to="/cv-reviewer/" replace />} />
                <Route path="/linkedin-analyzer" element={<Navigate to="/linkedin-analyzer/" replace />} />
                <Route path="/linkedin-analyzer/" element={<LinkedInAnalyzerPage />} />
                <Route path="/linkedin-analyzer/*" element={<LinkedInAnalyzerPage />} />
                {/* Catch-all */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
            </ErrorBoundary>
            <SiteChatbot />
          </BrowserRouter>
        </TooltipProvider>
      </ThemeProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;
